import { useCallback, useEffect, useRef, useState } from 'react';

export interface ServiceSelection {
	selectedIds: ReadonlySet<string>;
	/**
	 * Toggle one service. With `extendRange` (shift-click) every service between
	 * the last plain click and this one is selected instead — the range is taken
	 * from the currently visible order, which is what the provider sees.
	 */
	toggle: (id: string, extendRange: boolean) => void;
	/** Select everything currently matching the search and category filter. */
	selectVisible: () => void;
	/** Drop just the visible ones, leaving selections made under other filters. */
	deselectVisible: () => void;
	clear: () => void;
	/** True once a shift-click has actually extended a range. */
	hasUsedRange: boolean;
}

/**
 * Multi-select for the services grid.
 *
 * Selection deliberately survives search and filter changes: providers file
 * services by searching for a term, selecting the matches, searching again, and
 * assigning the lot in one go. Ids that disappear from `allIds` — a deleted
 * service — are dropped, so a stale id can never reach the assign endpoint.
 */
const useServiceSelection = (visibleIds: string[], allIds: string[]): ServiceSelection => {
	const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set<string>());
	const [hasUsedRange, setHasUsedRange] = useState(false);

	// The last plainly-clicked service, used as one end of a shift-click range.
	const anchorRef = useRef<string | null>(null);

	// Read inside callbacks so they don't need to be rebuilt every render.
	const visibleRef = useRef(visibleIds);
	visibleRef.current = visibleIds;

	const allIdsKey = allIds.join(',');
	useEffect(() => {
		const valid = new Set(allIdsKey ? allIdsKey.split(',') : []);
		setSelectedIds(prev => {
			const next = new Set([...prev].filter(id => valid.has(id)));
			return next.size === prev.size ? prev : next;
		});
	}, [allIdsKey]);

	const toggle = useCallback((id: string, extendRange: boolean) => {
		const visible = visibleRef.current;
		const anchor = anchorRef.current;

		if (extendRange && anchor && anchor !== id) {
			const from = visible.indexOf(anchor);
			const to = visible.indexOf(id);

			// Either end can have been filtered out since the anchor was set;
			// fall back to a plain toggle rather than guessing at a range.
			if (from !== -1 && to !== -1) {
				const [start, end] = from < to ? [from, to] : [to, from];
				const range = visible.slice(start, end + 1);
				setSelectedIds(prev => new Set([...prev, ...range]));
				setHasUsedRange(true);
				return;
			}
		}

		anchorRef.current = id;
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	const selectVisible = useCallback(() => {
		setSelectedIds(prev => new Set([...prev, ...visibleRef.current]));
	}, []);

	const deselectVisible = useCallback(() => {
		setSelectedIds(prev => {
			const visible = new Set(visibleRef.current);
			return new Set([...prev].filter(id => !visible.has(id)));
		});
	}, []);

	const clear = useCallback(() => {
		anchorRef.current = null;
		setSelectedIds(new Set<string>());
	}, []);

	return { selectedIds, toggle, selectVisible, deselectVisible, clear, hasUsedRange };
};

export default useServiceSelection;
